/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { deleteItem, update } from '../../services/item';
import { deepCloneArray } from '../../utils/array';
import { getId } from '../../utils/item';
import { isGlobalAdmin } from '../../utils/roles';
import { canUseMjForItem, isMjForItem } from '../../utils/auth';
import {
  getStoredAuthUser,
  hasAccessConfig,
  isUniverseAdmin,
} from '../../utils/auth';
import { ModalGlobal } from '../modal/modal.Global';
import { ColorsSwatch } from '../form/colorsSwatch';
import { BtnSimple } from '../form/btn.Simple';
import { IconPicker } from '../icons/icon.Picker';
import {
  DangerZone,
  SettingsInput,
  SettingsRow,
  SettingsSelect,
  SettingsSelectCustom,
  WidgetSettingsCollapsibleSection,
  WidgetSettingsRoot,
} from '../form/settings.Widget';
import { removeItem } from '../../store/items';
import { syncAutoTabsOnLinkAdded, syncAutoTabsOnLinkRemoved } from '../../utils/item';
import { Checkbox } from '../form/checkbox';
import { normalizeMapState } from '../board/map/boardMapDefaults.js';
// import { FaTimes } from 'react-icons/fa';

const TAB_DISPLAY_OPTIONS = [
  { value: 'grid', label: 'Grille' },
  { value: 'map', label: 'Tableau blanc' },
];

export const TabSettings = props => {
  const { tabId, itemId } = props;

  const tab = useSelector(state => state.items[tabId]);
  const item = useSelector(state => state.items[itemId]);
  const items = useSelector(state => state.items);
  const currentBoardViewport = useSelector(
    state => state.global.boardViewports?.[tabId] || null,
  );
  const canMj = isMjForItem(item, items) || canUseMjForItem(item, items);
  const dispatch = useDispatch();

  const [tabDisplay, setTabDisplay] = useState('grid');
  const [tabPosition, setTabPosition] = useState(null);
  const [tabColor, setTabColor] = useState('primary-d');
  const [campaigns, setCampaigns] = useState([]);
  const [universes, setUniverses] = useState([]);

  useEffect(() => {
    if (item?.tabs) {
      const tabConfig = item.tabs.find(tabObj => getId(tabObj) === tabId);
      if (tabConfig) {
        setTabDisplay(tabConfig[tabId]?.display);
        setTabPosition(tabConfig[tabId]?.position);
        setTabColor(tabConfig[tabId]?.color);
      }
    }
  }, [item?.tabs]);

  const getAllCampaigns = async universeId => {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/items/allcampaigns/${universeId}`,
    );
    if (res?.data) {
      res.data.sort((a, b) =>
        a.label < b.label ? -1 : a.label > b.label ? 1 : 0,
      );
      setCampaigns(res.data);
    }
  };

  const getUniverses = async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/items/universes`,
    );
    if (res?.data) {
      const mjAccess = window.localStorage.getItem('mj_access');
      const authUser = getStoredAuthUser();
      const authUserId = authUser?._id;
      const allowedUniverses = res.data.filter(universe =>
        isGlobalAdmin()
          ? true
          : hasAccessConfig(universe)
            ? isUniverseAdmin(universe, authUserId)
            : universe?.password === mjAccess,
      );
      allowedUniverses.sort((a, b) =>
        a.label < b.label ? -1 : a.label > b.label ? 1 : 0,
      );
      setUniverses(allowedUniverses);
    }
  };

  useEffect(() => {
    getUniverses();
  }, []);

  useEffect(() => {
    if (tab?.universe && tab?.universe !== 'non défini') {
      getAllCampaigns(tab.universe);
    } else {
      setCampaigns([]);
    }
  }, [tab?.universe]);

  const removeTab = () => {
    if (!item?._id || !Array.isArray(item?.tabs)) {
      return;
    }
    const targetTabId = tabId || tab?._id;
    if (!targetTabId) {
      return;
    }

    const nextTabs = deepCloneArray(item.tabs).filter(
      tabObj => getId(tabObj) !== targetTabId,
    );
    if (nextTabs.length === item.tabs.length) {
      return;
    }

    update(`items/${item._id}`, 'tabs', nextTabs);
    if (item?.sidebar === targetTabId) {
      update(`items/${item._id}`, 'sidebar', null);
    }
  };

  const canDeletePermanently = () => {
    // Special tabs aren't real "tab" items.
    const protectedTabs = new Set(['text', 'img', 'link', 'num', 'conditions']);
    if (protectedTabs.has(tabId)) return false;
    return tab?.type === 'tab';
  };

  const deleteTabPermanently = () => {
    if (!canMj) return;
    if (!tabId) return;

    // Remove from current item first to keep UI consistent immediately.
    removeTab();
    if (item?.sidebar === tabId) {
      update(`items/${itemId}`, 'sidebar', null);
    }

    deleteItem(tabId, () => {
      dispatch(removeItem(tabId));
    });
  };

  const updateDisplay = value => {
    const clonedTabs = deepCloneArray(item.tabs);
    const idx = item.tabs.findIndex(tabObj => getId(tabObj) === tabId);
    if (idx > -1) {
      clonedTabs[idx][tabId].display = value;
      update(`items/${itemId}`, 'tabs', clonedTabs);
    }
  };

  const updatePosition = value => {
    const clonedTabs = deepCloneArray(item.tabs);
    const idx = item.tabs.findIndex(tabObj => getId(tabObj) === tabId);
    if (idx > -1) {
      clonedTabs[idx][tabId].position = value;
      setTabPosition(value);
      update(`items/${itemId}`, 'tabs', clonedTabs);
    }
  };

  const updateColor = value => {
    const clonedTabs = deepCloneArray(item.tabs);
    const idx = item.tabs.findIndex(tabObj => getId(tabObj) === tabId);
    if (idx > -1) {
      clonedTabs[idx][tabId].color = value;
      update(`items/${itemId}`, 'tabs', clonedTabs);
    }
  };

  const updateAddToLinkedItems = async value => {
    const clonedTabs = deepCloneArray(item.tabs);
    const idx = item.tabs.findIndex(tabObj => getId(tabObj) === tabId);
    if (idx === -1) return;
    const previousItem = item;
    clonedTabs[idx][tabId].addToLinkedItems = value === true;
    const nextItem = { ...item, tabs: clonedTabs };
    await update(`items/${itemId}`, 'tabs', clonedTabs);

    if (Array.isArray(item?.links) && item.links.length > 0) {
      const linkedIds = item.links.map(linkObj => getId(linkObj)).filter(Boolean);
      await Promise.all(
        linkedIds.map(async linkedId => {
          const linkedItem = items?.[linkedId];
          if (!linkedItem) return;
          if (value === true) {
            await syncAutoTabsOnLinkAdded(nextItem, linkedItem);
          } else {
            await syncAutoTabsOnLinkRemoved(previousItem, linkedItem);
          }
        }),
      );
    }
  };

  const saveCurrentMapViewport = async () => {
    if (!tabId || !currentBoardViewport) {
      return;
    }
    const nextMap = {
      ...normalizeMapState(tab?.map),
      viewport: {
        x: Number(currentBoardViewport?.x) || 0,
        y: Number(currentBoardViewport?.y) || 0,
        zoom: Number(currentBoardViewport?.zoom) || 1,
      },
    };
    await update(`items/${tabId}`, 'map', nextMap);
  };

  return (
    <ModalGlobal
      idToOpen={tabId}
      title={`Edition de l'onglet ${tab?.label}`}
    >
      <WidgetSettingsRoot>
        <WidgetSettingsCollapsibleSection
          title='Général'
          defaultOpen={true}
        >
          <SettingsRow
            label='Label'
            dense
          >
            <SettingsInput
              placeholder={'label'}
              initValue={tab?.label}
              onBlur={value => update(`items/${tabId}`, 'label', value)}
            />
          </SettingsRow>

          <IconPicker
            size={24}
            value={tab?.icon}
            onChange={label => update(`items/${tabId}`, 'icon', label || '')}
            placeholder='search icon'
            label='Icon'
            previewSize={24}
            inputClassName='w-full'
            gridClassName='mx-1'
            maxHeight={150}
            className='flex flex-1 items-center gap-05 flex-wrap'
          />

          <SettingsRow
            label='Couleur'
            dense
            className='mt-05'
          >
            <ColorsSwatch
              onClick={color => updateColor(color)}
              colors={[
                'transparent',
                'primary-l',
                'secondary',
                'red',
                'orange',
                'yellow',
                'lime',
                'green',
                'blue',
                'purple',
                'pink',
              ]}
              currentColor={tabColor}
            />
          </SettingsRow>

          <SettingsRow
            label='Courte description'
            dense
          >
            <SettingsInput
              placeholder={'alias'}
              initValue={tab?.alias}
              onBlur={value => update(`items/${tabId}`, 'alias', value)}
            />
          </SettingsRow>
        </WidgetSettingsCollapsibleSection>

        {canMj && (
          <WidgetSettingsCollapsibleSection
            title='Affichage'
            defaultOpen={false}
          >
            <SettingsRow
              label='Affichage'
              dense
            >
              <SettingsSelectCustom
                className='w-full'
                options={TAB_DISPLAY_OPTIONS}
                value={tabDisplay || 'grid'}
                onChange={e => {
                  const nextValue = e.target.value;
                  setTabDisplay(nextValue);
                  updateDisplay(nextValue);
                }}
                placeholder='Affichage'
              />
            </SettingsRow>

            <SettingsRow
              label='Onglet en lecture seule'
              dense
            >
              <SettingsSelect
                onChange={e =>
                  update(
                    `items/${tabId}`,
                    'readOnlyMode',
                    e.target.value === 'off' ? null : e.target.value,
                  )
                }
                defaultValue={
                  tab?.readOnlyMode === 'soft'
                    ? 'soft'
                    : tab?.readOnlyMode === 'hard'
                      ? 'hard'
                      : 'off'
                }
              >
                <option value='off'>Aucune restriction</option>
                <option value='soft'>Mise en page verrouillée</option>
                <option value='hard'>Tout verrouiller</option>
              </SettingsSelect>
            </SettingsRow>

            <SettingsRow
              label='Afficher sur téléphone'
              dense
            >
              <Checkbox
                label='Activer'
                value={tabPosition === 'sidebar'}
                onChange={next => updatePosition(next ? 'sidebar' : null)}
              />
            </SettingsRow>

            <SettingsRow
              label='Ajouter aux items liés'
              dense
            >
              <Checkbox
                label='Activer'
                value={
                  item?.tabs?.find(tabObj => getId(tabObj) === tabId)?.[tabId]
                    ?.addToLinkedItems === true
                }
                onChange={next => updateAddToLinkedItems(next)}
              />
            </SettingsRow>

            {tabDisplay === 'map' && (
              <SettingsRow
                label='Vue par défaut (tableau blanc)'
                dense
              >
                <div className='flex w-full items-center justify-between gap-05'>
                  <div className='text-xs text-contrast text-opacity-60'>
                    Sauvegarde la position et le zoom courants.
                  </div>
                  <BtnSimple
                    className='whitespace-nowrap'
                    disabled={!currentBoardViewport}
                    onClick={saveCurrentMapViewport}
                  >
                    Sauvegarder la vue courante
                  </BtnSimple>
                </div>
              </SettingsRow>
            )}
          </WidgetSettingsCollapsibleSection>
        )}

        {canMj && (
          <WidgetSettingsCollapsibleSection
            title='Organisation'
            defaultOpen={false}
          >
            <SettingsRow
              label='Univers'
              dense
            >
              <SettingsSelect
                onChange={e => {
                  const nextUniverse = e.target.value;
                  update(`items/${tabId}`, 'universe', nextUniverse);
                  if (nextUniverse && nextUniverse !== 'non défini') {
                    getAllCampaigns(nextUniverse);
                  } else {
                    setCampaigns([]);
                  }
                }}
                defaultValue={
                  universes.some(universe => universe._id === tab?.universe)
                    ? tab?.universe
                    : 'non défini'
                }
              >
                <option value='non défini'>non défini</option>
                {universes.map(universe => (
                  <option
                    key={universe._id}
                    value={universe._id}
                  >
                    {universe.label}
                  </option>
                ))}
              </SettingsSelect>
            </SettingsRow>

            <SettingsRow
              label='Dossier'
              dense
            >
              <SettingsSelect
                onChange={e =>
                  update(`items/${tabId}`, 'campaign', e.target.value)
                }
                defaultValue={
                  campaigns.some(campaign => campaign._id === tab?.campaign)
                    ? tab?.campaign
                    : 'non défini'
                }
              >
                <option value='non défini'>non défini</option>
                {campaigns.map(campaign => (
                  <option
                    key={campaign._id}
                    value={campaign._id}
                  >
                    {campaign.label}
                  </option>
                ))}
              </SettingsSelect>
            </SettingsRow>
          </WidgetSettingsCollapsibleSection>
        )}

        <WidgetSettingsCollapsibleSection
          title='Actions'
          defaultOpen={false}
        >
          <div className='text-xs text-contrast text-opacity-60'>
            Double cliquez ci-dessous pour enlever l’onglet {tab?.label} de{' '}
            {item?.label} sans le supprimer définitivement.
          </div>
          <div className='flex justify-end'>
            <BtnSimple
              sm
              color='secondary'
              onDoubleClick={e => {
                e.stopPropagation();
                removeTab();
              }}
            >
              Enlever de cet item
            </BtnSimple>
          </div>
        </WidgetSettingsCollapsibleSection>

        {canMj && canDeletePermanently() && (
          <DangerZone
            title='Suppression définitive'
            confirmTitle='Suppression définitive'
            confirmText='Supprimer définitivement'
            confirmId={`deleteTab-${tabId}-${itemId}`}
            closeModalId={tabId}
            message='Voulez-vous vraiment supprimer définitivement cet onglet ? Attention: si cet onglet est utilisé ailleurs, il sera supprimé partout.'
            onConfirm={deleteTabPermanently}
          />
        )}
      </WidgetSettingsRoot>
    </ModalGlobal>
  );
};
